/**
 * Sprint Funnel · #2 — Analíticas del propietario.
 *
 * /admin/analytics
 *   · KPI cards: vistas, únicos, clicks (teléfono/WA/web), leads, conversión
 *   · Tabs de periodo: 7d / 30d / 90d / Todo
 *   · Gráfico de líneas SVG (sin lib pesada) con métrica seleccionable
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Eye, Users, Phone, WhatsappLogo, Globe, EnvelopeSimple,
  TrendUp, ArrowsClockwise, ChartLineUp,
} from "@phosphor-icons/react";

const PERIODS = [
  { id: 7,  label: "7 días" },
  { id: 30, label: "30 días" },
  { id: 90, label: "90 días" },
  { id: 0,  label: "Todo" },  // 0 = all-time
];

const METRICS = [
  { id: "profile_views",   label: "Vistas",         color: "#FFD700" },
  { id: "unique_visitors", label: "Únicos",         color: "#7DD3FC" },
  { id: "phone_clicks",    label: "Tel.",           color: "#A78BFA" },
  { id: "whatsapp_clicks", label: "WhatsApp",       color: "#25D366" },
  { id: "leads_created",   label: "Leads",          color: "#F472B6" },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState("profile_views");
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async (_days, _metric) => {
    setLoading(true);
    try {
      const [sumRes, tsRes] = await Promise.all([
        api.get(`/admin/analytics/summary?days=${_days}`),
        api.get(`/admin/analytics/timeseries?days=${_days <= 0 ? 365 : _days}&metric=${_metric}`),
      ]);
      setSummary(sumRes.data);
      setSeries(tsRes.data?.series || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh(days, metric); /* eslint-disable-line react-hooks/exhaustive-deps */
  }, [days, metric]);

  const s = summary;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">Mi visibilidad</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-1">Analíticas</h1>
          <p className="text-sm text-[var(--ag-text-dim)] mt-1">
            Mide cómo te ven los clientes potenciales en tu perfil público.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refresh(days, metric)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--ag-text-dim)] hover:text-white border border-[var(--ag-border)] rounded transition-colors"
          data-testid="analytics-refresh-btn"
        >
          <ArrowsClockwise size={14} /> Refrescar
        </button>
      </header>

      {/* Period tabs */}
      <div className="flex flex-wrap gap-2" data-testid="analytics-period-tabs">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setDays(p.id)}
            data-testid={`period-${p.id}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              days === p.id
                ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)]"
                : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading || !s ? (
        <div className="p-10 text-center text-[var(--ag-text-dim)] text-sm" data-testid="analytics-loading">Cargando…</div>
      ) : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpis">
            <Kpi icon={Eye}            label="Vistas perfil"  value={s.profile_views}   testId="kpi-views"   accent />
            <Kpi icon={Users}          label="Visitantes únicos" value={s.unique_visitors} testId="kpi-unique" />
            <Kpi icon={Phone}          label="Clicks teléfono"   value={s.phone_clicks}  testId="kpi-phone" />
            <Kpi icon={WhatsappLogo}   label="Clicks WhatsApp"   value={s.whatsapp_clicks} testId="kpi-wa" color="#25D366" />
            <Kpi icon={Globe}          label="Clicks web"        value={s.website_clicks} testId="kpi-web" />
            <Kpi icon={EnvelopeSimple} label="Leads recibidos"   value={s.leads_created} testId="kpi-leads" accent />
            <Kpi icon={TrendUp}        label="Conv. visita→lead" value={`${s.conversion_pct}%`} testId="kpi-conversion" hint="Porcentaje de visitas que envían un lead" />
            <Kpi icon={ChartLineUp}    label="Tasa de contacto"  value={`${s.contact_pct}%`}    testId="kpi-contact" hint="% de visitas que hacen click en algún CTA" />
          </div>

          {/* Chart */}
          <div className="border border-[var(--ag-border)] rounded-lg p-4 bg-[var(--ag-midnight-soft)]/40" data-testid="analytics-chart-card">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-semibold">Evolución</h3>
              <div className="flex flex-wrap gap-1.5" data-testid="metric-selector">
                {METRICS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMetric(m.id)}
                    data-testid={`metric-${m.id}`}
                    className={`px-2 py-1 text-[11px] font-medium rounded border transition-colors ${
                      metric === m.id ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)]" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <LineChart series={series} color={METRICS.find((m) => m.id === metric)?.color || "#FFD700"} />
          </div>
        </>
      )}
    </div>
  );
}

/* ============================ subcomponents ================== */
function Kpi({ icon: Icon, label, value, testId, accent, color, hint }) {
  return (
    <div
      className={`p-3 rounded-lg border ${accent ? "border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/5" : "border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]/40"}`}
      data-testid={testId}
      title={hint || undefined}
    >
      <div className="flex items-center gap-2">
        <Icon size={16} weight="duotone" style={color ? { color } : undefined} className={accent ? "text-[var(--ag-gold)]" : "text-[var(--ag-text-dim)]"} />
        <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{label}</div>
      </div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function LineChart({ series, color }) {
  if (!series || series.length === 0) {
    return <div className="h-40 grid place-items-center text-xs text-[var(--ag-text-dim)] italic">Sin datos aún. Comparte tu perfil para empezar a recibir visitas.</div>;
  }
  const w = 720;
  const h = 160;
  const padL = 30, padR = 8, padT = 12, padB = 18;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const max = Math.max(1, ...series.map((p) => p.value));
  const stepX = innerW / Math.max(1, series.length - 1);
  const points = series.map((p, i) => {
    const x = padL + i * stepX;
    const y = padT + innerH - (p.value / max) * innerH;
    return [x, y, p];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${path} L${(padL + innerW).toFixed(1)},${(padT + innerH).toFixed(1)} L${padL.toFixed(1)},${(padT + innerH).toFixed(1)} Z`;

  const total = series.reduce((a, b) => a + b.value, 0);
  const firstDay = series[0]?.date;
  const lastDay = series[series.length - 1]?.date;

  return (
    <div data-testid="analytics-linechart">
      <div className="flex items-baseline justify-between text-xs mb-1">
        <span className="text-[var(--ag-text-dim)] tabular-nums">{firstDay} → {lastDay}</span>
        <span className="font-bold tabular-nums" style={{ color }}>{total} total</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40">
        <defs>
          <linearGradient id="ag-chart-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid */}
        {[0, 0.5, 1].map((t) => (
          <line key={t} x1={padL} x2={padL + innerW} y1={padT + innerH * t} y2={padT + innerH * t}
            stroke="rgba(255,255,255,0.06)" strokeDasharray="3,3" />
        ))}
        {/* Area */}
        <path d={area} fill="url(#ag-chart-grad)" />
        {/* Line */}
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* Y-axis labels */}
        <text x={4} y={padT + 4} fontSize="9" fill="rgba(255,255,255,0.5)" textAnchor="start">{max}</text>
        <text x={4} y={padT + innerH + 3} fontSize="9" fill="rgba(255,255,255,0.5)" textAnchor="start">0</text>
        {/* Dots cada N días */}
        {points.map(([x, y, p], i) => (
          (series.length <= 30 || i % Math.ceil(series.length / 30) === 0) && (
            <circle key={i} cx={x} cy={y} r="2.5" fill={color}>
              <title>{p.date}: {p.value}</title>
            </circle>
          )
        ))}
      </svg>
    </div>
  );
}
